import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { LogicBuilder } from './../../models/logic-builder/logic-builder';
import { DESIGN_CONSTANTS } from './../../shared/constants/design-constants';

import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { ConfirmationDialogComponent } from '../../components';
import {
    Action,
    ActionTypeDropdownOption, App,
    DialogModel,
    FieldIdentifier,
    LogicBuilderEmailPreviewData,
    LogicBuilderFormModel,
    LogicBuilderSet,
    LogicBuilderSetFormModel,
    MappedPinColorSelection,
    PresetStyleColorSelection,
    Project, RecursiveTriggerContainer, RecursiveTriggerContainerFormModel,
    SelectableField, Site, Trigger, User
} from '../../models';
import { MapPinColorSelection } from '../../models/logic-builder/map-pin-color-selection';
import { ActionType, FieldType, RecipientType, SiteTypes, TriggerOperator, TriggerRelationship } from '../../shared/enums';
import { LogicBuilderDataService } from '../data-services';
import { KHThemeService } from '../tools/theme.service';
import { ColorsUtilityService } from '../utility-services/colors-utility.service';
import { LogicBuilderFormServiceModel } from './models/logic-builder-form-model';

@Injectable({
    providedIn: 'root'
})
export class LogicBuilderFormLogicService {

    private _updateActionModeledField: Subject<boolean> = new Subject<boolean>();
    private _formTriedSaving: Subject<boolean> = new Subject<boolean>();
    private _formIsSaved: Subject<boolean> = new Subject<boolean>();

    updateActionModeledField$ = this._updateActionModeledField.asObservable();
    formTriedSaving$ = this._formTriedSaving.asObservable();
    formIsSaved$ = this._formIsSaved.asObservable();

    form: UntypedFormGroup;
    model: LogicBuilderFormServiceModel;
    logicBuilder: LogicBuilder = null;

    get logicBuilderSets(): UntypedFormArray {
        return this.form.get('logicBuilderSets') as UntypedFormArray;
    }

    get showAddActionButton(): boolean {
        return this.getActionControls(0)?.length < this.model.maxSupportedActionsPerLogicSet;
    }

    get showAddTriggerButton(): boolean {
        return this.model.triggersCount < this.model.maxSupportedTriggersPerLogicSet;
    }

    constructor(
        private _colorsUtilityService: ColorsUtilityService,
        private _logicBuilderDataService: LogicBuilderDataService,
        private _themeService: KHThemeService,
        private _snackBar: MatSnackBar,
        private _sanitizer: DomSanitizer,
        private _dialog: MatDialog
    ) { }

    initModelForNewLogicBuilder(app: App, sites: Site[], fields: SelectableField[], currentProject: Project, users: User[], currentUser: User, pullFieldsFromSmartsheet: string[], setFields: FieldIdentifier[], styleFields: FieldIdentifier[], triggerFieldsFromOtherLogicBuilders: FieldIdentifier[], isMapPinUsed: boolean): void {
        this.logicBuilder = null;
        this.initModel(app, sites, fields, currentProject, users, currentUser, pullFieldsFromSmartsheet, setFields, styleFields, triggerFieldsFromOtherLogicBuilders, isMapPinUsed);
        this.setModeledSitesForNewLogicBuilder();
        this.initFormForNewLogicBuilder();
        this.model.logicBuilderTitle = this.model.newLogicBuilderTitle;
        this.initAllDependentProperties();
    }

    initModelForExistingLogicBuilder(logicBuilder: LogicBuilder, app: App, sites: Site[], fields: SelectableField[], currentProject: Project, users: User[], currentUser: User, pullFieldsFromSmartsheet: string[], setFields: FieldIdentifier[], styleFields: FieldIdentifier[], triggerFieldsFromOtherLogicBuilders: FieldIdentifier[], isMapPinUsed: boolean): void {
        this.logicBuilder = { ...logicBuilder };
        this.initModel(app, sites, fields, currentProject, users, currentUser, pullFieldsFromSmartsheet, setFields, styleFields, triggerFieldsFromOtherLogicBuilders, isMapPinUsed);
        this.setModeledSitesForExistingLogicBuilder(logicBuilder);
        this.initFormForExistingLogicBuilder(logicBuilder);
        this.model.logicBuilderTitle = this.model.existingLogicBuilderTitle;
        this.initAllDependentProperties();
    }

    initFormForNewLogicBuilder(): void {
        this.form = new UntypedFormGroup({
            active: new UntypedFormControl(true),
            createdBy: new UntypedFormControl(),
            description: new UntypedFormControl(null),
            id: new UntypedFormControl(null),
            name: new UntypedFormControl(null, [Validators.required]),
            projectId: new UntypedFormControl(null),
            siteIds: new UntypedFormArray([]),
            logicBuilderSets: new UntypedFormArray([])
        });

        this.addInitialLogicBuilderSet();
    }

    initFormForExistingLogicBuilder(logicBuilder: LogicBuilder): void {
        this.form = new UntypedFormGroup({
            active: new UntypedFormControl(logicBuilder.active),
            createdById: new UntypedFormControl(logicBuilder.createdById),
            createdByName: new UntypedFormControl(logicBuilder.createdByName),
            description: new UntypedFormControl(logicBuilder.description),
            id: new UntypedFormControl(logicBuilder.id),
            logicBuilderSets: new UntypedFormArray([]),
            name: new UntypedFormControl(logicBuilder.name, [Validators.required]),
            projectId: new UntypedFormControl(logicBuilder.projectId, [Validators.required]),
            siteIds: new UntypedFormControl(logicBuilder.siteIds ?? []),
            siteEmailNotificationsTriggered: new UntypedFormControl(logicBuilder.siteEmailNotificationsTriggered ?? [])
        });

        logicBuilder.logicBuilderSets.forEach((logicBuilderSet: LogicBuilderSet, logicSetIndex: number) => {
            const logicBuilderSetForm = new UntypedFormGroup({
                actions: new UntypedFormArray([]),
                triggers: new UntypedFormArray([])
            });

            this.initializeActionsForExistingLogicBuilderSet(logicBuilderSet, logicBuilderSetForm);

            this.initializeTriggersForExistingLogicBuilderSet(logicBuilderSet, logicBuilderSetForm, logicSetIndex);

            this.logicBuilderSets?.push(logicBuilderSetForm);
        });
    }

    addActionForm(logicSetIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        const actionControls = this.getActionControls(logicSetIndex);
        if (!actionControls) {
            return;
        }

        actionControls.push(this.createActionForm());

        this.handleUpdateShouldAllowHasChangedTriggerOperator();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    addTriggerForm(logicSetIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        const triggerContainers = this.getTriggerControls(logicSetIndex);
        if (!triggerContainers) {
            return;
        }

        const newTriggerContainer = this.createTriggerContainerForm();

        triggerContainers.push(newTriggerContainer);

        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    removeAction(logicSetIndex: number, actionIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm || actionsForm.length <= 1) {
            return;
        }

        this.getActionControls(logicSetIndex)?.removeAt(actionIndex);

        this.handleUpdateShouldAllowHasChangedTriggerOperator();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    removeTrigger(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        const triggersForm = this.getTriggerControls(logicSetIndex);
        if (!triggersForm) {
            return;
        }

        const triggerContainerForm = triggersForm.at(containerIndex) as UntypedFormGroup;
        const nestedTriggerContainerForm = triggerContainerForm?.get('trigger') as UntypedFormArray;
        if (!nestedTriggerContainerForm) {
            return;
        }

        if (triggersForm.length === 1 && nestedTriggerContainerForm.length === 1) {
            return;
        }

        if (nestedTriggerContainerForm.length === 1) {
            this.removeTriggerContainer(triggersForm, containerIndex);
        }
        else {
            this.removeNestedTriggerContainer(triggersForm, triggerContainerForm, nestedTriggerContainerForm, containerIndex, triggerIndex);
        }

        this.handleUpdateSelectedGridId();
        this.handleUpdateShouldAllowStyleActionTypes();
        this.handleUpdateShouldAllowSetActionTypes();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();

        this.model.triggersCount--;
    }

    groupTriggerAbove(logicSetIndex: number, selectedIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        this.groupTriggerForm(logicSetIndex, selectedIndex - 1, selectedIndex);
    }

    groupTriggerBelow(logicSetIndex: number, selectedIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        this.groupTriggerForm(logicSetIndex, selectedIndex, selectedIndex + 1);
    }

    unGroupTriggers(logicSetIndex: number, containerIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        const triggerContainers = this.getTriggerControls(logicSetIndex);
        if (!triggerContainers) {
            return;
        }

        const triggerContainerForm = triggerContainers.at(containerIndex) as UntypedFormGroup;

        const nestedTriggerContainerForm = triggerContainerForm?.get('trigger') as UntypedFormArray;
        if (!nestedTriggerContainerForm || nestedTriggerContainerForm.length === 1) {
            return;
        }

        this.unGroupTriggersHelper(triggerContainers, triggerContainerForm, nestedTriggerContainerForm, containerIndex);
    }

    addDynamicStylingLogicSet(): void {
        if (!this.model.shouldAllowAddDynamicStyleFieldLogicSet && !this.model.shouldAllowAddDynamicMapPinStylingLogicSet) {
            return;
        }

        const referenceLogicBuilderSet = this.getLogicBuilderSet(0);
        if (!referenceLogicBuilderSet) {
            return;
        }

        const referenceActions = referenceLogicBuilderSet.get('actions') as UntypedFormArray;
        const referenceTriggers = referenceLogicBuilderSet.get('triggers') as UntypedFormArray;

        const newActions = new UntypedFormArray([]);
        const newTriggers = new UntypedFormArray([]);

        referenceActions.controls.forEach((referenceAction: UntypedFormGroup) => {
            newActions.push(this.copyActionForDynamicLogicSet(referenceAction));
        });

        referenceTriggers.controls.forEach((referenceTrigger: UntypedFormGroup) => {
            newTriggers.push(this.copyTriggerContainerForDynamicLogicSet(referenceTrigger));
        });

        const logicBuilderSet = new UntypedFormGroup({
            actions: newActions,
            triggers: newTriggers
        });

        this.logicBuilderSets?.push(logicBuilderSet);

        this.setAvailableFieldStyleColors();
        this.setAvailableMapPinColors();
    }

    removeDynamicStylingLogicSet(index: number): void {
        if (this.logicBuilderSets.length <= 1 || index < 1 || index >= this.logicBuilderSets.length) {
            return;
        }

        this.logicBuilderSets.removeAt(index);

        this.setAvailableFieldStyleColors();
        this.setAvailableMapPinColors();
    }

    relationshipChanged(): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
    }

    saveChanges(app: App): void {
        if (!this.model.doesLogicBuilderContainDynamicLogic) {
            this.goBackFromDynamicLogic();
            this.logicBuilderSets.controls.splice(1);
            this.logicBuilderSets.updateValueAndValidity();
            this.form.updateValueAndValidity();
        }

        if (!this.form.valid) {
            this.formTriedSaving();
            return;
        }

        const selectedSites = this.model.siteDropdownInfo.selectedSites;
        if (!selectedSites.length) {
            this.formTriedSaving();
            return;
        }

        const logicBuilder = this.convertFormToLogicBuilder();

        const doesLogicBuilderContainAllSites = selectedSites.includes(this.model.siteDropdownInfo.selectAllSite.id);
        if (doesLogicBuilderContainAllSites) {
            delete logicBuilder.siteIds;
        }
        else {
            logicBuilder.siteIds = [...selectedSites];
        }

        if (this.couldEmailNotificationsBeReset(logicBuilder)) {
            const dialogData = new DialogModel();
            dialogData.title = 'Email Notification Behavior';
            dialogData.message = 'You are updating a logic builder that impacts a grid. How should we handle emails for this change? <ul><li><b>Resend for existing rows: </b>Send emails for all rows that currently meet the trigger conditions. </li><li><b>Future matches only: </b> Send emails only when a row meets the conditions from now on.</li></ul>';
            dialogData.confirmationActionTitle = 'Resend for existing rows';
            dialogData.dismissActionTitle = 'Future matches only';
            const dialogRef = this._dialog.open(ConfirmationDialogComponent, { width: DESIGN_CONSTANTS.DIALOG_WIDTH_700PX, data: { dialogData }, disableClose: true, autoFocus: false });

            dialogRef.afterClosed().subscribe((resendExistingRows: boolean | undefined) => {
                if (resendExistingRows === undefined) {
                    return;
                }

                if (resendExistingRows) {
                    this.resetEmailNotificationSentList(logicBuilder);
                    this.createOrUpdateLogicBuilder(app, logicBuilder);
                    return;
                }

                this.createOrUpdateLogicBuilder(app, logicBuilder);
            });
            return;
        }

        this.createOrUpdateLogicBuilder(app, logicBuilder);
    }

    createOrUpdateLogicBuilder(app: App, logicBuilder: LogicBuilder): void {
        this.model.formIsSaving = true;

        if (logicBuilder.id) {
            this._logicBuilderDataService.updateLogicBuilder(app, logicBuilder)
                .subscribe({
                    next: () => {
                        this._snackBar.open('Logic set successfully updated', 'Close', { duration: 2000 });
                    },
                    error: () => {
                        this._snackBar.open('An error has occurred updating the logic set', 'Close', { duration: 2000 });
                    },
                    complete: () => {
                        this._formIsSaved.next(true);
                        this.model.formIsSaving = false;
                    }
                });
            return;
        }

        logicBuilder.createdById = this.model.currentUser.id;
        logicBuilder.createdByName = this.model.currentUser.name;
        logicBuilder.projectId = this.model.currentProject.id;

        this._logicBuilderDataService.createLogicBuilder(app, logicBuilder)
            .subscribe({
                next: () => {
                    this._snackBar.open('Logic set successfully created', 'Close', { duration: 2000 });
                },
                error: () => {
                    this._snackBar.open('An error has occurred creating the logic set', 'Close', { duration: 2000 });
                },
                complete: () => {
                    this._formIsSaved.next(true);
                    this.model.formIsSaving = false;
                }
            });
    }

    goBackFromDynamicLogic(): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = false;
        this.model.doesLogicBuilderContainDynamicLogic = false;
        this.model.doesLogicBuilderContainDynamicMapPinStyling = false;
        this.model.doesLogicBuilderContainDynamicStyleFieldLogic = false;
    }

    switchToDynamicStyleFieldLogic(): void {
        if (this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic && this.logicBuilderSets.length > 1) {
            this.logicBuilderSets.controls.splice(1);
            this.logicBuilderSets.updateValueAndValidity();
            this.form.updateValueAndValidity();
        }

        this.model.doesLogicBuilderContainDynamicLogic = true;
        this.model.doesLogicBuilderContainDynamicStyleFieldLogic = false;
    }

    switchToDynamicMapPinStylingLogic(): void {
        if (this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic && this.logicBuilderSets.length > 1) {
            this.logicBuilderSets.controls.splice(1);
            this.logicBuilderSets.updateValueAndValidity();
            this.form.updateValueAndValidity();
        }

        this.model.doesLogicBuilderContainDynamicLogic = true;
        this.model.doesLogicBuilderContainDynamicMapPinStyling = true;
    }

    triggerSelectedFieldChanged(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        this.resetFieldDependentTriggerProperties(logicSetIndex, containerIndex, triggerIndex);

        this.handleUpdateSelectedGridId();
        this.handleUpdateShouldAllowStyleActionTypes();
        this.handleUpdateShouldAllowSetActionTypes();
        this.setAvailableActionTypes();

        const actionControls = this.getActionControls(0);
        if (!actionControls || actionControls.length < 1) {
            this.setShouldAllowAddDynamicStyleFieldLogicSet();
            this.setShouldAllowAddDynamicMapPinStylingLogicSet();
            return;
        }

        const action = actionControls.controls.find((action: UntypedFormGroup) => {
            return action.get('selectedField').value?.fieldId && (
                action.get('actionType').value === ActionType.SetField ||
                action.get('actionType').value === ActionType.StyleFieldColor
            );
        });

        if (!action) {
            this.setShouldAllowAddDynamicStyleFieldLogicSet();
            this.setShouldAllowAddDynamicMapPinStylingLogicSet();
            return;
        }

        const actionIndex = actionControls.controls.indexOf(action);
        if (actionIndex < 0) {
            this.setShouldAllowAddDynamicStyleFieldLogicSet();
            this.setShouldAllowAddDynamicMapPinStylingLogicSet();
            return;
        }

        const styleField = this.model.availableFieldsToStyle.find((field: SelectableField) => {
            return field.identifier.fieldId === action.get('selectedField').value?.fieldId &&
                field.identifier.gridColumnId === action.get('selectedField').value?.gridColumnId;
        });

        const setField = this.model.availableSetFields.find((field: SelectableField) => {
            return field.identifier.fieldId === action.get('selectedField').value?.fieldId &&
                field.identifier.gridColumnId === action.get('selectedField').value?.gridColumnId;
        });

        if (!styleField?.enabled || !setField?.enabled) {
            this.resetDependentActionPropertiesAndValidity(0, actionIndex);
            this.setShouldAllowAddDynamicStyleFieldLogicSet();
            this.setShouldAllowAddDynamicMapPinStylingLogicSet();
            return;
        }

        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    triggerOperatorChanged(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        this.resetOperatorDependentTriggerPropertiesAndValidity(logicSetIndex, containerIndex, triggerIndex);

        this.handleUpdateShouldAllowStyleActionTypes();
        this.handleUpdateShouldAllowSetActionTypes();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    actionTypeChanged(logicSetIndex: number, actionIndex: number): void {
        this.model.shouldCropDynamicLogicSetsOnSwitchToDynamicLogic = true;
        this.resetDependentActionPropertiesAndValidity(logicSetIndex, actionIndex);

        this.handleUpdateShouldAllowHasChangedTriggerOperator();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
        this.resetDependentActionPropertiesAndValidity(logicSetIndex, actionIndex);
    }

    actionFieldChanged(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionFieldPropertiesAndValidity(logicSetIndex, actionIndex);
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
    }

    actionSetDateTypeChanges(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionSetDateTypePropertiesAndValidity(logicSetIndex, actionIndex);
    }

    actionEmailRecieptientTypeChanged(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionEmailRecieptientTypePropertiesAndValidity(logicSetIndex, actionIndex);
    }

    initAllDependentProperties(): void {
        this.handleUpdateSelectedGridId();
        this.handleUpdateShouldAllowStyleActionTypes();
        this.handleUpdateShouldAllowSetActionTypes();
        this.handleUpdateShouldAllowHasChangedTriggerOperator();
        this.setAvailableActionTypes();
        this.setShouldAllowAddDynamicStyleFieldLogicSet();
        this.setShouldAllowAddDynamicMapPinStylingLogicSet();
        this.setAvailableFieldStyleColors();
        this.setAvailableMapPinColors();
        this.setIsDynamicLogic();
    }

    setAvailableTriggerFields(): void {
        if (!this.model.selectedGridId) {
            this.model.availableTriggerFields = this.model.allFields.map((field: SelectableField) => {
                return {
                    ...field,
                    enabled: !this.model.setFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                        return field.identifier.fieldId === setField.fieldId &&
                            field.identifier.gridColumnId === setField.gridColumnId;
                    })
                };
            }).sort((field => field.enabled ? -1 : 1));
            return;
        }

        this.model.availableTriggerFields = [];
        this.model.availableTriggerFields = this.model.allFields.map((field: SelectableField) => {
            return {
                ...field,
                enabled: !this.model.setFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                    return field.identifier.fieldId === setField.fieldId &&
                        field.identifier.gridColumnId === setField.gridColumnId;
                }) && (field.identifier.fieldId === this.model.selectedGridId || !field.identifier.gridColumnId)
            };
        }).sort((field => field.enabled ? -1 : 1));
    }

    setAvailableFieldsToStyle(): void {
        if (!this.model.selectedGridId) {
            this.model.availableFieldsToStyle = this.model.allFields.map((field: SelectableField) => {
                return {
                    ...field,
                    enabled: !field.identifier.gridColumnId &&
                        !this.model.styleFieldsFromOtherLogicBuilders.some((styleField: FieldIdentifier) => {
                            return field.identifier.fieldId === styleField.fieldId &&
                                field.identifier.gridColumnId === styleField.gridColumnId;
                        })
                };
            }).sort((field => field.enabled ? -1 : 1));
            return;
        }

        this.model.availableFieldsToStyle = this.model.allFields.map((field: SelectableField) => {
            return {
                ...field,
                enabled: field.identifier.fieldId === this.model.selectedGridId &&
                    !this.model.styleFieldsFromOtherLogicBuilders.some((styleField: FieldIdentifier) => {
                        return field.identifier.fieldId === styleField.fieldId &&
                            field.identifier.gridColumnId === styleField.gridColumnId;
                    })
            };
        }).sort((field => field.enabled ? -1 : 1));
    }

    setAvailableActionSetFields(): void {
        if (!this.model.selectedGridId) {
            this.model.availableSetFields = this.model.supportedSetFields.map((field: SelectableField) => {
                return {
                    ...field,
                    enabled: (!field.identifier.gridColumnId) &&
                        !this.model.pullFieldsFromSmartsheet.includes(field.identifier.fieldId) &&
                        !this.model.setFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                            return field.identifier.fieldId === setField.fieldId &&
                                field.identifier.gridColumnId === setField.gridColumnId;
                        }) &&
                        !this.model.triggerFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                            return field.identifier.fieldId === setField.fieldId &&
                                field.identifier.gridColumnId === setField.gridColumnId;
                        })
                };
            }).sort((field => field.enabled ? -1 : 1));
            return;
        }

        this.model.availableSetFields = this.model.supportedSetFields.map((field: SelectableField) => {
            return {
                ...field,
                enabled: (field.identifier.fieldId === this.model.selectedGridId) &&
                    !this.model.setFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                        return field.identifier.fieldId === setField.fieldId &&
                            field.identifier.gridColumnId === setField.gridColumnId;
                    }) &&
                    !this.model.triggerFieldsFromOtherLogicBuilders.some((setField: FieldIdentifier) => {
                        return field.identifier.fieldId === setField.fieldId &&
                            field.identifier.gridColumnId === setField.gridColumnId;
                    })
            };
        }).sort((field => field.enabled ? -1 : 1));
    }

    setAvailableFieldsToSetValuesFrom(): void {
        if (!this.model.selectedGridId) {
            this.model.availableFieldsToSetValuesFrom = this.model.supportedFieldsToSetValuesFrom.filter((field: SelectableField) => !field.identifier.gridColumnId);
            return;
        }

        this.model.availableFieldsToSetValuesFrom = this.model.supportedFieldsToSetValuesFrom.filter((field: SelectableField) => field.identifier.fieldId === this.model.selectedGridId);
    }

    setAvailableEmailRecipientFields(): void {
        if (!this.model.selectedGridId) {
            this.model.availableEmailRecipientFields = this.model.allFields.filter((field: SelectableField) => {
                return !field.identifier.gridColumnId && field.fieldTypeId === FieldType.Person;
            });
            return;
        }

        this.model.availableEmailRecipientFields = this.model.allFields.filter((field: SelectableField) => {
            return (field.identifier.fieldId === this.model.selectedGridId ||
                !field.identifier.gridColumnId)
                && field.fieldTypeId === FieldType.Person;
        });
    }

    setAvailableEmailTemplateFields(): void {
        if (!this.model.selectedGridId) {
            this.model.availableEmailTemplateFields = this.model.allFields.filter((field: SelectableField) => !field.identifier.gridColumnId);
        }
        else {
            this.model.availableEmailTemplateFields = this.model.allFields.filter((field: SelectableField) => field.identifier.fieldId === this.model.selectedGridId);
        }
        const siteNameField: SelectableField = {
            fieldTypeId: FieldType.TextBox,
            enabled: true,
            identifier: {
                fieldId: 'siteName',
                gridColumnId: null,
            },
            name: `${this.model.siteAlias} name`,
            hash: 'siteName',
            uniqueReplacementHtmlValue: `<span class='khts-html-replaceable-field' id='siteName'>${this.model.siteAlias} name</span>`,
            uniqueReplacementValue: '{siteName}',
            icon: 'location_on',
            breadcrumb: ``,
        };
        const projectNameField: SelectableField = {
            fieldTypeId: FieldType.TextBox,
            enabled: true,
            identifier: {
                fieldId: 'projectName',
                gridColumnId: null,
            },
            name: `${this.model.projectAlias} name`,
            hash: 'projectName',
            uniqueReplacementHtmlValue: `<span class='khts-html-replaceable-field' id='projectName'>${this.model.projectAlias} name</span>`,
            uniqueReplacementValue: '{projectName}',
            icon: 'folder',
            breadcrumb: ``,
        };
        const appNameField: SelectableField = {
            fieldTypeId: FieldType.TextBox,
            enabled: true,
            identifier: {
                fieldId: 'appName',
                gridColumnId: null,
            },
            name: `App name`,
            hash: 'appName',
            uniqueReplacementHtmlValue: `<span class='khts-html-replaceable-field' id='appName'>App name</span>`,
            uniqueReplacementValue: '{appName}',
            icon: 'home',
            breadcrumb: ``,
        };

        this.model.availableEmailTemplateFields.unshift(siteNameField);
        this.model.availableEmailTemplateFields.unshift(projectNameField);
        this.model.availableEmailTemplateFields.unshift(appNameField);
    }

    setAvailableActionTypes(): void {
        this.model.availableActionTypes = new Map<number, ActionTypeDropdownOption[]>();
        const actionsControls = this.getActionControls(0);

        if (!actionsControls) {
            return;
        }

        this.model.actionTypes.forEach(actionType => {
            actionType.isUsed = false;
        });

        actionsControls.controls.forEach((actionControl: UntypedFormGroup) => {
            const actionType = actionControl.get('actionType').value;
            if (!actionType) {
                return;
            }

            const actionTypeOption = this.model.actionTypes.find((actionTypeOption: ActionTypeDropdownOption) => actionTypeOption.id === actionType);
            if (!actionTypeOption) {
                return;
            }

            actionTypeOption.isUsed = true;
        });

        actionsControls.controls.forEach((actionControl, index) => {
            const availableActionTypes = this.model.actionTypes.filter((actionTypeOption: ActionTypeDropdownOption) => {
                return (
                    !actionTypeOption.isUsed ||
                    actionTypeOption.id === actionControl.get('actionType').value
                );
            });
            this.model.availableActionTypes.set(index, availableActionTypes);
        });
    }

    shouldAllowStyleActionTypes(): boolean {
        const triggerControls = this.getTriggerControls(0);

        if (!triggerControls?.length) {
            return false;
        }

        return !this.doesTriggersContainerHaveHasChangedOperator(triggerControls);
    }

    shouldAllowSetActionTypes(): boolean {
        const triggerControls = this.getTriggerControls(0);

        if (!triggerControls?.length) {
            return false;
        }

        if (this.doesTriggersContainerHaveHasChangedOperator(triggerControls)) {
            return false;
        }

        if (this.doesTriggersContainerHaveFormulaField(triggerControls)) {
            return false;
        }

        return true;
    }

    shouldAllowHasChangedTriggerOperator(): boolean {
        const actionControls = this.getActionControls(0);
        if (!actionControls || actionControls.length < 1) {
            return false;
        }

        for (let i = 0; i < actionControls.length; i++) {
            const actionType = actionControls.at(i).get('actionType').value;
            if (actionType === ActionType.StyleFieldColor || actionType === ActionType.SetMapPinColor || actionType === ActionType.SetField) {
                return false;
            }
        }

        return true;
    }

    shouldAllowAddDynamicStyleFieldLogicSet(): boolean {
        if (!this.model.shouldAllowStyleActionTypes) {
            return false;
        }

        const actionControls = this.getActionControls(0);
        if (!actionControls || actionControls.length !== 1) {
            return false;
        }

        if (actionControls.at(0).get('actionType').value !== ActionType.StyleFieldColor ||
            !actionControls.at(0).get('selectedField').value?.fieldId) {
            return false;
        }

        const triggerControls = this.getTriggerControls(0);

        if (!triggerControls?.length) {
            return false;
        }

        return this.doesTriggersContainerHaveEverySelectedFieldAndRequiredRelationshipsPopulated(triggerControls);
    }

    shouldAllowAddDynamicMapPinStylingLogicSet(): boolean {
        if (!this.model.shouldAllowStyleActionTypes) {
            return false;
        }

        const actionControls = this.getActionControls(0);
        if (!actionControls || actionControls.length !== 1) {
            return false;
        }

        if (actionControls.at(0).get('actionType').value !== ActionType.SetMapPinColor) {
            return false;
        }

        const triggerControls = this.getTriggerControls(0);

        if (!triggerControls?.length) {
            return false;
        }

        return this.doesTriggersContainerHaveEverySelectedFieldAndRequiredRelationshipsPopulated(triggerControls);
    }

    getSelectedGridId(): string {
        const triggerControls = this.getTriggerControls(0);

        if (!triggerControls?.length) {
            return '';
        }

        let selectedGridId = '';
        for (let i = 0; i < triggerControls.length; i++) {
            selectedGridId = this.recursivelyFindGridIdFromTriggerContainer(triggerControls.at(i) as UntypedFormGroup);
            if (selectedGridId) {
                break;
            }
        }

        return selectedGridId;
    }

    filterSites(filter: string): void {
        this.model.siteDropdownInfo.filteredSites = this.model.siteDropdownInfo.availableSites.filter((site: Site) => {
            return site.dropdownName.toLowerCase().includes(filter.toLowerCase());
        });
    }

    handleAssociateSiteWithLogicBuilder(): void {
        const siteIdToAssociate: string = this.model.siteDropdownInfo.modeledSites.find(siteId => {
            return !this.model.siteDropdownInfo.selectedSites.includes(siteId);
        });

        if (siteIdToAssociate === this.model.siteDropdownInfo.selectAllSiteNo) {
            this.associateAllSitesWithLogicBuilder();
            return;
        }

        this.associateSiteWithLogicBuilder(siteIdToAssociate);
    }

    disassociateSiteFromLogicBuilder(): void {
        const siteIdToDisassociate: string = this.model.siteDropdownInfo.selectedSites.find(siteId => {
            return !this.model.siteDropdownInfo.modeledSites.includes(siteId);
        });

        if (siteIdToDisassociate === this.model.siteDropdownInfo.selectAllSiteNo) {
            this.disassociateAllSitesFromLogicBuilder();
            return;
        }

        this.model.siteDropdownInfo.selectedSites = this.model.siteDropdownInfo.selectedSites.filter((siteId: string) => {
            return siteId !== siteIdToDisassociate &&
                siteId !== this.model.siteDropdownInfo.selectAllSite.id;
        });

        this.model.siteDropdownInfo.modeledSites = [...this.model.siteDropdownInfo.selectedSites];
        this.model.siteDropdownInfo.numberOfSelectedSites--;
    }

    setAvailableFieldStyleColors(): void {
        this.model.availableFieldStyleColorOptions = new Map<number, PresetStyleColorSelection[]>();

        if (this.logicBuilderSets.length === 1) {
            this.model.availableFieldStyleColorOptions.set(0, this.model.fieldStyleColorOptions);
            return;
        }

        this.model.fieldStyleColorOptions.forEach((fieldStyleColorOption: PresetStyleColorSelection) => {
            fieldStyleColorOption.isUsed = false;
        });

        this.logicBuilderSets.controls.forEach((logicBuilderSet: UntypedFormGroup) => {
            const actions = logicBuilderSet.get('actions') as UntypedFormArray;
            if (!actions?.length) {
                return;
            }

            const foundAction = actions.controls.find((action: UntypedFormGroup) => {
                return action.get('actionType').value === ActionType.StyleFieldColor;
            });

            if (!foundAction) {
                return;
            }

            const fieldStyleColorOption = this.model.fieldStyleColorOptions.find((fieldStyleColorOption: PresetStyleColorSelection) => {
                return fieldStyleColorOption.id === foundAction.get('valueToSet').value;
            });

            if (!fieldStyleColorOption) {
                return;
            }
            fieldStyleColorOption.isUsed = true;
        });

        this.logicBuilderSets.controls.forEach((logicBuilderSet: UntypedFormGroup, index: number) => {
            const actions = logicBuilderSet.get('actions') as UntypedFormArray;
            if (!actions?.length) {
                return;
            }

            const foundAction = actions.controls.find((action: UntypedFormGroup) => {
                return action.get('actionType').value === ActionType.StyleFieldColor;
            });

            if (!foundAction) {
                const unUsedFieldStyleColors = this.model.fieldStyleColorOptions.filter((fieldStyleColorOption: PresetStyleColorSelection) => {
                    return !fieldStyleColorOption.isUsed;
                });
                this.model.availableFieldStyleColorOptions.set(index, unUsedFieldStyleColors);
                return;
            }

            const availableFieldStyleColors = this.model.fieldStyleColorOptions.filter((fieldStyleColorOption: PresetStyleColorSelection) => {
                return !fieldStyleColorOption.isUsed || fieldStyleColorOption.id === foundAction.get('valueToSet')?.value;
            });

            this.model.availableFieldStyleColorOptions.set(index, availableFieldStyleColors);
        });
    }

    setAvailableMapPinColors(): void {
        this.model.availableMapPinColorOptions = new Map<number, MapPinColorSelection[]>();

        if (this.logicBuilderSets.length === 1) {
            this.model.availableMapPinColorOptions.set(0, this.model.mapPinColorOptions);
            return;
        }

        this.model.mapPinColorOptions.forEach((mapPinColorOption: MapPinColorSelection) => {
            mapPinColorOption.isUsed = false;
        });

        this.logicBuilderSets.controls.forEach((logicBuilderSet: UntypedFormGroup) => {
            const actions = logicBuilderSet.get('actions') as UntypedFormArray;
            if (!actions?.length) {
                return;
            }

            const foundAction = actions.controls.find((action: UntypedFormGroup) => {
                return action.get('actionType').value === ActionType.SetMapPinColor;
            });

            if (!foundAction) {
                return;
            }

            const mapPinColorOption = this.model.mapPinColorOptions.find((mapPinColorOption: MapPinColorSelection) => {
                return mapPinColorOption.id === foundAction.get('valueToSet').value;
            });

            if (!mapPinColorOption) {
                return;
            }

            mapPinColorOption.isUsed = true;
        });

        this.logicBuilderSets.controls.forEach((logicBuilderSet: UntypedFormGroup, index: number) => {
            const actions = logicBuilderSet.get('actions') as UntypedFormArray;

            const actionType = actions.controls.find((action: UntypedFormGroup) => {
                return action.get('actionType').value === ActionType.SetMapPinColor;
            });

            if (!actionType) {
                const unUsedMapPinColors = this.model.mapPinColorOptions.filter((mapPinColorOption: MapPinColorSelection) => {
                    return !mapPinColorOption.isUsed;
                });
                this.model.availableMapPinColorOptions.set(index, unUsedMapPinColors);
                return;
            }

            const availableMapPinColors = this.model.mapPinColorOptions.filter((mapPinColorOption: MapPinColorSelection) => {
                return !mapPinColorOption.isUsed || mapPinColorOption.id === actionType.get('valueToSet')?.value;
            });

            this.model.availableMapPinColorOptions.set(index, availableMapPinColors);
        });

    }

    convertFormToLogicBuilder(): LogicBuilder {
        const logicBuilderFormModel = this.form.getRawValue() as LogicBuilderFormModel;
        return this.getLogicBuilderFromLogicBuilderFormModel(logicBuilderFormModel);
    }

    getActionTypeIcon(actionTypeId: number): string {
        switch (actionTypeId) {
            case ActionType.StyleFieldColor:
                return 'font_download';
            case ActionType.SetField:
                return 'wand_shine';
            case ActionType.SetMapPinColor:
                return 'location_on';
            case ActionType.SendEmail:
                return 'mail';
            default:
                return '';
        }
    }

    getFieldStyleColorIcon(fieldStyleColor: number): string {
        const fieldStyleColorOption = this.model.fieldStyleColorOptions.find((fieldStyleColorOption: PresetStyleColorSelection) => {
            return fieldStyleColorOption.id === fieldStyleColor;
        });

        return fieldStyleColorOption ? fieldStyleColorOption.class : '';
    }

    getMapPinIcon(mapPinColorId: number): string {
        const mapPinColorOption = this.model.mapPinColorOptions.find((mapPinColorOption: MapPinColorSelection) => {
            return mapPinColorOption.id === mapPinColorId;
        });

        return mapPinColorOption?.mapPinIcon ?? '';
    }

    hasLogicSetTriggersBeenVisited(index: number): boolean {
        const logicBuilderSet = this.getLogicBuilderSet(index);
        if (!logicBuilderSet) {
            return false;
        }

        const triggerControls = logicBuilderSet.get('triggers') as UntypedFormArray;
        if (!triggerControls?.length) {
            return false;
        }

        let wasTriggersVisited = false;
        for (let i = 0; i < triggerControls.length; i++) {
            wasTriggersVisited = this.recursivelyFindIfTriggersWhereVisitedFromTriggerContainer(triggerControls.at(i) as UntypedFormGroup);
            if (wasTriggersVisited) {
                break;
            }
        }

        return wasTriggersVisited;
    }

    isSaveEnabled(): boolean {
        if (!this.model?.siteDropdownInfo?.modeledSites?.length) {
            return false;
        }

        if (this.model?.doesLogicBuilderContainDynamicLogic) {
            return this.form?.valid;
        }

        return this.logicBuilderSets.at(0)?.valid &&
            this.form.get('name')?.valid;
    }

    shouldEnablePreviewButton(): boolean {
        if (!this.form.valid) {
            return false;
        }

        const selectedSites = this.model.siteDropdownInfo.selectedSites;
        if (!selectedSites.length) {
            return false;
        }

        return true;
    }

    generateEmailPreview(app: App, project: Project): void {
        if (!this.shouldEnablePreviewButton()) {
            return;
        }

        const logicBuilder = this.convertFormToLogicBuilder();
        let firstSite = this.model.siteDropdownInfo?.availableSites?.find((site: Site) => {
            return site?.id !== this.model.siteDropdownInfo.selectAllSite.id &&
                site?.active && site?.showSite;
        })?.id;

        if (!firstSite) {
            const selectedSites = this.model.siteDropdownInfo.selectedSites;
            firstSite = selectedSites.find((siteId: string) => {
                return siteId !== this.model.siteDropdownInfo.selectAllSite.id;
            });
        }

        if (!firstSite) {
            return;
        }

        const emailAction = logicBuilder.logicBuilderSets[0].actions.find((action: Action) => {
            return action.actionType === ActionType.SendEmail;
        });

        this.model.triggersResponsible = [];

        logicBuilder.logicBuilderSets[0].triggers.forEach((trigger: RecursiveTriggerContainer) => {
            this.addTriggersToTriggerList(trigger);
        });

        const LogicBuilderEmailPreviewData: LogicBuilderEmailPreviewData = {
            emailProperties: emailAction.emailProperties,
            gridRowId: null,
            selectableFields: this.model.allFields,
            siteId: firstSite,
            triggersResponsible: this.model.triggersResponsible,
        };

        this.model.isLoadingEmailPreview = true;
        this._logicBuilderDataService.getLogicBuilderPreview(app, project, LogicBuilderEmailPreviewData)
            .subscribe({
                next: (previewHtml: any) => {
                    this.model.emailPreviewContent = this._sanitizer.bypassSecurityTrustHtml(previewHtml.HtmlEmailPreview);
                },
                error: () => {
                    this.model.emailPreviewContent = this._sanitizer.bypassSecurityTrustHtml('');
                    this._snackBar.open('An error has occurred generating the email preview', 'Close', { duration: 2000 });
                },
                complete: () => {
                    this.model.isLoadingEmailPreview = false;
                }
            });
    }

    private addTriggersToTriggerList(triggerContainer: RecursiveTriggerContainer): void {
        if (triggerContainer.trigger) {
            this.model.triggersResponsible.push(triggerContainer.trigger);
        }

        triggerContainer.nestedTriggerContainers?.forEach((nestedTriggerContainer: RecursiveTriggerContainer) => {
            this.addTriggersToTriggerList(nestedTriggerContainer);
        });
    }

    private getActionControls(index: number): UntypedFormArray {
        return this.getLogicBuilderSet(index)?.get('actions') as UntypedFormArray;
    }

    private getTriggerControls(index: number): UntypedFormArray {
        return this.getLogicBuilderSet(index)?.get('triggers') as UntypedFormArray;
    }

    private getLogicBuilderSet(index: number): UntypedFormGroup {
        return (this.logicBuilderSets.at(index) as UntypedFormGroup);
    }

    private initModel(app: App, sites: Site[], fields: SelectableField[], currentProject: Project, users: User[], currentUser: User, pullFieldsFromSmartsheet: string[], setFields: FieldIdentifier[], styleFields: FieldIdentifier[], triggerFieldsFromOtherLogicBuilders: FieldIdentifier[], isMapPinUsed: boolean): void {
        this.model = new LogicBuilderFormServiceModel();
        this.model.allFields = fields;
        this.model.currentProject = currentProject;
        this.model.currentUser = currentUser;
        this.model.users = users;
        this.model.pullFieldsFromSmartsheet = pullFieldsFromSmartsheet ?? [];
        this.model.isMapPinUsed = isMapPinUsed;
        this.model.siteAlias = app?.aliases?.site?.singular ?? 'Site';
        this.model.projectAlias = app?.aliases?.project?.singular ?? 'Project';

        this.setTriggerAndSetFieldsForOtherLogicBuilders(setFields, styleFields, triggerFieldsFromOtherLogicBuilders);
        this.setInitialModelProperties();
        this.setAvailableSites(app, currentProject, sites);
    }

    private setInitialModelProperties(): void {
        this.setInitialMapPinColors();
        this.setInitialFieldStyleColors();
        this.setSupportedSetFields();
        this.setSupportedFieldsToSetValuesFrom();
    }

    private setTriggerAndSetFieldsForOtherLogicBuilders(setFields: FieldIdentifier[], styleFields: FieldIdentifier[], triggerFields: FieldIdentifier[]): void {
        this.model.triggerFieldsFromOtherLogicBuilders = triggerFields ?? [];

        if (!this.logicBuilder) {
            this.model.setFieldsFromOtherLogicBuilders = setFields ?? [];
            this.model.styleFieldsFromOtherLogicBuilders = styleFields ?? [];
        }

        let actionWithSetField = null;
        let actionWithStyleField = null;
        this.logicBuilder?.logicBuilderSets.forEach((logicBuilderSet: LogicBuilderSet) => {
            logicBuilderSet.actions?.forEach((action: Action) => {
                if (action.actionType === ActionType.SetField) {
                    actionWithSetField = action;
                }

                if (action.actionType === ActionType.StyleFieldColor) {
                    actionWithStyleField = action;
                }
            });
        });

        if (!actionWithSetField) {
            this.model.setFieldsFromOtherLogicBuilders = setFields ?? [];
        }
        else {
            this.model.setFieldsFromOtherLogicBuilders = setFields.filter((setField: FieldIdentifier) => {
                setField.fieldId !== actionWithSetField.selectedField?.fieldId &&
                    setField.gridColumnId !== actionWithSetField.selectedField?.gridColumnId;
            }) ?? [];
        }

        if (!actionWithStyleField) {
            this.model.styleFieldsFromOtherLogicBuilders = styleFields ?? [];
        }
        else {
            this.model.styleFieldsFromOtherLogicBuilders = styleFields.filter((styleField: FieldIdentifier) => {
                styleField.fieldId !== actionWithStyleField.selectedField?.fieldId &&
                    styleField.gridColumnId !== actionWithStyleField.selectedField?.gridColumnId;
            }) ?? [];
        }
    }

    private setInitialMapPinColors(): void {
        this.model.mapPinColorOptions = [];
        this._colorsUtilityService.model.mapPinColors.forEach((mapPinColor: MappedPinColorSelection, key: number) => {
            const mapPinColorSelection: MapPinColorSelection = new MapPinColorSelection();
            mapPinColorSelection.color = mapPinColor.color;
            mapPinColorSelection.mapPinIcon = this._themeService.buildColorizedSvgFromHex(mapPinColorSelection.color);
            mapPinColorSelection.name = mapPinColor.name;
            mapPinColorSelection.id = key;
            mapPinColorSelection.isUsed = false;
            this.model.mapPinColorOptions.push(mapPinColorSelection);
        });

        this.model.availableMapPinColorOptions = new Map<number, MapPinColorSelection[]>();
        this.model.availableMapPinColorOptions.set(0, this.model.mapPinColorOptions);
    }

    private setInitialFieldStyleColors(): void {
        this.model.fieldStyleColorOptions = [];
        this._colorsUtilityService.model.fieldStyleColors.forEach((fieldStyleColor: PresetStyleColorSelection, key: number) => {
            const fieldStyleColorSelection: PresetStyleColorSelection = new PresetStyleColorSelection();
            fieldStyleColorSelection.class = fieldStyleColor.class;
            fieldStyleColorSelection.name = fieldStyleColor.name;
            fieldStyleColorSelection.id = key;
            fieldStyleColorSelection.isUsed = false;
            this.model.fieldStyleColorOptions.push(fieldStyleColorSelection);
        });

        this.model.availableFieldStyleColorOptions = new Map<number, PresetStyleColorSelection[]>();
        this.model.availableFieldStyleColorOptions.set(0, this.model.fieldStyleColorOptions);
    }

    private setSupportedSetFields(): void {
        this.model.supportedSetFields = this.model.allFields.filter((field: SelectableField) => {
            return this.model.supportedFieldTypesForSetField.includes(field.fieldTypeId);
        });
    }

    private setSupportedFieldsToSetValuesFrom(): void {
        this.model.supportedFieldsToSetValuesFrom = this.model.allFields.filter((field: SelectableField) => {
            return field.fieldTypeId === FieldType.Person;
        });
    }

    private addInitialLogicBuilderSet(): void {
        const logicBuilderSet = new UntypedFormGroup({
            actions: new UntypedFormArray([this.createActionForm()]),
            triggers: new UntypedFormArray([this.createInitialTriggerContainerForm()])
        });

        this.logicBuilderSets?.push(logicBuilderSet);
    }

    private initializeActionsForExistingLogicBuilderSet(logicBuilderSet: LogicBuilderSet, logicBuilderSetForm: UntypedFormGroup): void {
        logicBuilderSet.actions?.forEach((action: Action) => {
            const actionForm = new UntypedFormGroup({
                actionType: new UntypedFormControl(action.actionType, [Validators.required]),
                selectedField: new UntypedFormControl({
                    fieldId: action.selectedField?.fieldId,
                    gridColumnId: action.selectedField?.gridColumnId,
                },
                    action.selectedField?.fieldId ? [Validators.required] : []
                ),
                setDateToToday: new UntypedFormControl(action.setDateToToday, action.setDateToToday ? [Validators.required] : []),
                valueToSet: new UntypedFormControl(action.valueToSet, action.valueToSet ? [Validators.required] : []),
                emailProperties: new UntypedFormGroup({
                    recipientType: new UntypedFormControl(
                        action.emailProperties?.recipientType,
                        action.emailProperties?.recipientType ? [Validators.required] : []
                    ),
                    recipientField: new UntypedFormControl({
                        fieldId: action.emailProperties?.recipientField?.fieldId,
                        gridColumnId: action.emailProperties?.recipientField?.gridColumnId,
                    },
                        action.emailProperties?.recipientField?.fieldId ? [Validators.required] : []
                    ),
                    recipientList: new UntypedFormControl(
                        action.emailProperties?.recipientList,
                        action.emailProperties?.recipientList ? [Validators.required] : []
                    ),
                    sendWithBcc: new UntypedFormControl(action.emailProperties?.sendWithBcc),
                    richTextSubjectTemplate: new UntypedFormControl(
                        action.emailProperties?.richTextSubjectTemplate,
                        action.emailProperties?.richTextSubjectTemplate ? [Validators.required] : []
                    ),
                    richTextBodyTemplate: new UntypedFormControl(
                        action.emailProperties?.richTextBodyTemplate
                    )
                }),
            });

            const foundIndexForSelectedField = this.model.allFields.findIndex((field: SelectableField) => {
                return field.identifier.fieldId === action.selectedField?.fieldId &&
                    field.identifier.gridColumnId === action.selectedField?.gridColumnId;
            });

            if (foundIndexForSelectedField < 0) {
                actionForm.get('selectedField').setValue(null);

                actionForm.get('selectedField').markAsDirty();
                actionForm.get('selectedField').updateValueAndValidity();
            }

            const foundRecipientField = this.model.allFields.find((field: SelectableField) => {
                return field.identifier.fieldId === action.emailProperties?.recipientField?.fieldId &&
                    field.identifier.gridColumnId === action.emailProperties?.recipientField?.gridColumnId;
            });

            if (!foundRecipientField) {
                actionForm.get('emailProperties')?.get('recipientField').setValue(null);

                actionForm.get('emailProperties')?.get('recipientField').markAsDirty();
                actionForm.get('emailProperties')?.get('recipientField').updateValueAndValidity();
            }

            let recipientList = action.emailProperties?.recipientList;
            if (recipientList?.length) {
                recipientList = recipientList.filter((recipient: string) => {
                    return this.model.users.find((user: User) => {
                        return user.id === recipient;
                    });
                });
                if (recipientList.length === 0) {
                    recipientList = null;
                }
                actionForm.get('emailProperties')?.get('recipientList').setValue(recipientList);
                actionForm.get('emailProperties')?.get('recipientList').markAsDirty();
                actionForm.get('emailProperties')?.get('recipientList').updateValueAndValidity();
            }

            (logicBuilderSetForm.get('actions') as UntypedFormArray)?.push(actionForm);

            if (action.actionType === ActionType.StyleFieldColor) {
                const fieldStyleColorOption = this.model.fieldStyleColorOptions.find((fieldStyleColorOption: PresetStyleColorSelection) => {
                    return fieldStyleColorOption.id === action.valueToSet;
                });
                if (fieldStyleColorOption) {
                    fieldStyleColorOption.isUsed = true;
                }
            }

            if (action.actionType === ActionType.SetMapPinColor) {
                const maPinColorOption = this.model.mapPinColorOptions.find((mapPinColorOption: MapPinColorSelection) => {
                    return mapPinColorOption.id === action.valueToSet;
                });
                if (maPinColorOption) {
                    maPinColorOption.isUsed = true;
                }
            }

        });
    }

    private initializeTriggersForExistingLogicBuilderSet(logicBuilderSet: LogicBuilderSet, logicBuilderSetForm: UntypedFormGroup, logicSetIndex: number): void {
        logicBuilderSet.triggers?.forEach((triggerContainer: RecursiveTriggerContainer,) => {
            const triggerContainerForm = new UntypedFormGroup({
                trigger: new UntypedFormArray([]),
                relationshipToPreceding: new UntypedFormControl(
                    triggerContainer.relationshipToPreceding,
                    triggerContainer.relationshipToPreceding ? [Validators.required] : []
                )
            });

            this.createTriggerRecursiveFormForExistingLogicBuilder(triggerContainer, triggerContainerForm, logicSetIndex);
            (logicBuilderSetForm.get('triggers') as UntypedFormArray)?.push(triggerContainerForm);
        });
    }

    private createTriggerRecursiveFormForExistingLogicBuilder(triggerContainer: RecursiveTriggerContainer, triggerContainerForm: UntypedFormGroup, logicSetIndex: number): void {
        const triggerArray = triggerContainerForm.get('trigger') as UntypedFormArray;

        if (triggerContainer.trigger) {
            if (!logicSetIndex) {
                this.model.triggersCount++;
            }
            const triggerForm = new UntypedFormGroup({
                comparisonValue: new UntypedFormControl(triggerContainer.trigger?.comparisonValue,
                    triggerContainer.trigger?.comparisonValue ? [Validators.required] : []
                ),
                operator: new UntypedFormControl(triggerContainer.trigger?.operator, Validators.required),
                selectedField: new UntypedFormControl({
                    fieldId: triggerContainer.trigger?.selectedField?.fieldId,
                    gridColumnId: triggerContainer.trigger?.selectedField?.gridColumnId,
                },
                    triggerContainer.trigger?.selectedField?.fieldId ? [Validators.required] : []
                ),
            });

            const foundIndexForSelectedField = this.model.allFields.findIndex((field: SelectableField) => {
                return field.identifier.fieldId === triggerContainer.trigger?.selectedField?.fieldId &&
                    field.identifier.gridColumnId === triggerContainer.trigger?.selectedField?.gridColumnId;
            });
            if (foundIndexForSelectedField < 0) {
                triggerForm.get('selectedField').setValue(null);

                triggerForm.get('selectedField').markAsDirty();
                triggerForm.get('selectedField').updateValueAndValidity();
            }

            triggerArray.push(triggerForm);
            return;
        }

        triggerContainer.nestedTriggerContainers?.forEach((nestedTrigger: RecursiveTriggerContainer) => {
            const nestedTriggerContainerForm = new UntypedFormGroup({
                trigger: new UntypedFormArray([]),
                relationshipToPreceding: new UntypedFormControl(
                    nestedTrigger.relationshipToPreceding,
                    nestedTrigger.relationshipToPreceding ? [Validators.required] : []
                )
            });
            this.createTriggerRecursiveFormForExistingLogicBuilder(nestedTrigger, nestedTriggerContainerForm, logicSetIndex);
            triggerArray.push(nestedTriggerContainerForm);
        });
    }

    private createActionForm(): UntypedFormGroup {
        return new UntypedFormGroup({
            actionType: new UntypedFormControl(null, Validators.required),
            selectedField: new UntypedFormControl({
                fieldId: null,
                gridColumnId: null,
            },
                [Validators.required]
            ),
            setDateToToday: new UntypedFormControl(true),
            valueToSet: new UntypedFormControl(null),
            emailProperties: new UntypedFormGroup({
                recipientType: new UntypedFormControl(RecipientType.List),
                recipientField: new UntypedFormControl({
                    fieldId: null,
                    gridColumnId: null,
                }),
                recipientList: new UntypedFormControl(null),
                sendWithBcc: new UntypedFormControl(false),
                richTextSubjectTemplate: new UntypedFormControl(null),
                richTextBodyTemplate: new UntypedFormControl(null)
            })
        });
    }

    private createInitialTriggerContainerForm(): UntypedFormGroup {
        return new UntypedFormGroup({
            trigger: new UntypedFormArray([this.createTriggerForm()]),
            relationshipToPreceding: new UntypedFormControl(null)
        });

    }

    private createTriggerContainerForm(): UntypedFormGroup {
        return new UntypedFormGroup({
            trigger: new UntypedFormArray([this.createTriggerForm()]),
            relationshipToPreceding: new UntypedFormControl(TriggerRelationship.And, [Validators.required])
        });
    }

    private createTriggerForm(): UntypedFormGroup {
        this.model.triggersCount++;

        return new UntypedFormGroup({
            comparisonValue: new UntypedFormControl(null, []),
            operator: new UntypedFormControl(null, [Validators.required]),
            selectedField: new UntypedFormControl({
                fieldId: null,
                gridColumnId: null,
            },
                [Validators.required]
            ),
        });
    }

    private removeTriggerContainer(triggersForm: UntypedFormArray, containerIndex: number): void {
        triggersForm.removeAt(containerIndex);
        if (containerIndex) {
            return;
        }

        const firstContainer = triggersForm.at(containerIndex) as UntypedFormGroup;
        this.removeTriggerRelationshipToPrecedingForInitialContainerBeingMoved(firstContainer);
    }

    private removeNestedTriggerContainer(
        triggersForm: UntypedFormArray, triggerContainerForm: UntypedFormGroup, nestedTriggerContainerForm: UntypedFormArray, containerIndex: number, triggerIndex: number
    ): void {
        nestedTriggerContainerForm.removeAt(triggerIndex);

        const lastNestedTrigger = nestedTriggerContainerForm.at(0) as UntypedFormGroup;

        if (containerIndex) {
            this.setTriggerRelationshipToPrecedingForNonInitialContainerBeingMoved(triggerContainerForm, lastNestedTrigger);
        }
        else {
            this.removeTriggerRelationshipToPrecedingForInitialContainerBeingMoved(lastNestedTrigger);
        }

        triggersForm.removeAt(containerIndex);
        triggersForm.insert(containerIndex, lastNestedTrigger);
    }

    private groupTriggerForm(logicSetIndex: number, firstIndex: number, secondIndex: number): void {
        if (firstIndex < 0 || firstIndex >= secondIndex) {
            return;
        }

        const triggerContainers = this.getTriggerControls(logicSetIndex);
        if (!triggerContainers || secondIndex > triggerContainers.length - 1) {
            return;
        }

        const firstContainer = triggerContainers.at(firstIndex) as UntypedFormGroup;
        const secondContainer = triggerContainers.at(secondIndex) as UntypedFormGroup;

        triggerContainers.removeAt(secondIndex);
        triggerContainers.removeAt(firstIndex);

        const newTriggerContainer = new UntypedFormGroup({
            trigger: new UntypedFormArray([firstContainer, secondContainer]),
            relationshipToPreceding: new UntypedFormControl(
                firstContainer.get('relationshipToPreceding').value,
                firstIndex ? [Validators.required] : []
            )
        });

        firstContainer.get('relationshipToPreceding').setValue(null);
        firstContainer.get('relationshipToPreceding').clearValidators();
        firstContainer.get('relationshipToPreceding').updateValueAndValidity();

        triggerContainers.insert(firstIndex, newTriggerContainer);
    }

    private unGroupTriggersHelper(
        triggersForm: UntypedFormArray, triggerContainerForm: UntypedFormGroup, nestedTriggerContainerForm: UntypedFormArray, containerIndex: number
    ): void {
        const firstNestedTrigger = nestedTriggerContainerForm.at(0) as UntypedFormGroup;

        if (containerIndex) {
            this.setTriggerRelationshipToPrecedingForNonInitialContainerBeingMoved(triggerContainerForm, firstNestedTrigger);
        }
        else {
            this.removeTriggerRelationshipToPrecedingForInitialContainerBeingMoved(firstNestedTrigger);
        }

        triggersForm.removeAt(containerIndex);

        while (nestedTriggerContainerForm.length > 0) {
            const triggerContainerFormToMove = nestedTriggerContainerForm.at(nestedTriggerContainerForm.length - 1) as UntypedFormGroup;
            nestedTriggerContainerForm.removeAt(nestedTriggerContainerForm.length - 1);

            triggersForm.insert(containerIndex, triggerContainerFormToMove);
        }
    }

    private copyActionForDynamicLogicSet(referenceAction: UntypedFormGroup): UntypedFormGroup {
        const emailProperties = referenceAction.get('emailProperties') as UntypedFormGroup;
        const newAction = new UntypedFormGroup({
            actionType: new UntypedFormControl(referenceAction.get('actionType').value, [Validators.required]),
            selectedField: new UntypedFormControl({
                fieldId: referenceAction.get('selectedField').value?.fieldId,
                gridColumnId: referenceAction.get('selectedField').value?.gridColumnId,
            },
                []
            ),
            setDateToToday: new UntypedFormControl(null, []),
            valueToSet: new UntypedFormControl(null, [Validators.required]),
            emailProperties: new UntypedFormGroup({
                recipientType: new UntypedFormControl(
                    emailProperties.get('recipientType').value,
                ),
                recipientField: new UntypedFormControl({
                    fieldId: emailProperties?.get('recipientField')?.value?.fieldId,
                    gridColumnId: emailProperties?.get('recipientField')?.value?.gridColumnId,
                },
                ),
                recipientList: new UntypedFormControl(
                    emailProperties?.get('recipientList').value,
                ),
                sendWithBcc: new UntypedFormControl(emailProperties?.get('sendWithBcc')?.value),
                richTextSubjectTemplate: new UntypedFormControl(
                    emailProperties?.get('richTextSubjectTemplate')?.value,
                ),
                richTextBodyTemplate: new UntypedFormControl(
                    emailProperties?.get('richTextBodyTemplate')?.value,
                )
            }),
        });

        return newAction;
    }

    private copyTriggerContainerForDynamicLogicSet(referenceTrigger: UntypedFormGroup): UntypedFormGroup {
        const newTriggerContainer = new UntypedFormGroup({
            trigger: new UntypedFormArray([]),
            relationshipToPreceding: new UntypedFormControl(referenceTrigger.get('relationshipToPreceding').value, [])
        });

        const referenceTriggerArray = referenceTrigger.get('trigger') as UntypedFormArray;
        if (!referenceTriggerArray?.length) {
            return;
        }

        const newTriggerArray = newTriggerContainer.get('trigger') as UntypedFormArray;

        referenceTriggerArray.controls.forEach((trigger: UntypedFormGroup) => {
            if (trigger.contains('trigger')) {
                newTriggerArray.push(this.copyTriggerContainerForDynamicLogicSet(trigger));
                return;
            }

            const newTrigger = new UntypedFormGroup({
                comparisonValue: new UntypedFormControl(null, []),
                operator: new UntypedFormControl(null, [Validators.required]),
                selectedField: new UntypedFormControl({
                    fieldId: trigger.get('selectedField').value?.fieldId,
                    gridColumnId: trigger.get('selectedField').value?.gridColumnId,
                },
                    [Validators.required]
                ),
            });

            newTriggerArray.push(newTrigger);
        });

        return newTriggerContainer;
    }

    private removeTriggerRelationshipToPrecedingForInitialContainerBeingMoved(triggerContainer: UntypedFormGroup): void {
        triggerContainer.get('relationshipToPreceding').setValue(null);
        triggerContainer.get('relationshipToPreceding').clearValidators();
        triggerContainer.get('relationshipToPreceding').updateValueAndValidity();
    }

    private setTriggerRelationshipToPrecedingForNonInitialContainerBeingMoved(triggerContainerForm: UntypedFormGroup, triggerContainer: UntypedFormGroup): void {
        triggerContainer.get('relationshipToPreceding').setValue(triggerContainerForm.get('relationshipToPreceding').value);
        triggerContainer.get('relationshipToPreceding').addValidators(Validators.required);
        triggerContainer.get('relationshipToPreceding').updateValueAndValidity();
    }

    private handleUpdateSelectedGridId(): void {
        this.setSelectedGridId();
        this.setAvailableTriggerFields();
        this.setAvailableEmailRecipientFields();
        this.setAvailableEmailTemplateFields();
        this.setAvailableActionSetFields();
        this.setAvailableFieldsToStyle();
        this.setAvailableFieldsToSetValuesFrom();
    }

    private setSelectedGridId(): void {
        this.model.selectedGridId = this.getSelectedGridId();
    }

    private handleUpdateShouldAllowStyleActionTypes(): void {
        this.setShouldAllowStyleActionTypes();
        this.setIsEnabledActionTypes();
    }

    private handleUpdateShouldAllowSetActionTypes(): void {
        this.setShouldAllowSetActionTypes();
        this.setIsEnabledActionTypes();
    }

    private setShouldAllowSetActionTypes(): void {
        this.model.shouldAllowSetActionTypes = this.shouldAllowSetActionTypes();
    }

    private setShouldAllowStyleActionTypes(): void {
        this.model.shouldAllowStyleActionTypes = this.shouldAllowStyleActionTypes();
    }

    private handleUpdateShouldAllowHasChangedTriggerOperator(): void {
        this.setShouldAllowHasChangedTriggerOperator();
        this.setAvailableTriggerOperators();
    }

    private setShouldAllowHasChangedTriggerOperator(): void {
        this.model.shouldAllowHasChangedTriggerOperator = this.shouldAllowHasChangedTriggerOperator();
    }

    private setShouldAllowAddDynamicStyleFieldLogicSet(): void {
        this.model.shouldAllowAddDynamicStyleFieldLogicSet = this.shouldAllowAddDynamicStyleFieldLogicSet();
    }

    private setShouldAllowAddDynamicMapPinStylingLogicSet(): void {
        this.model.shouldAllowAddDynamicMapPinStylingLogicSet = this.shouldAllowAddDynamicMapPinStylingLogicSet();
    }

    private setAvailableTriggerOperators(): void {
        let selectableHasChangedOperator;

        selectableHasChangedOperator = this.model.dateSelectTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.checkBoxTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.dropdownTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.personTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.uploaderTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.numberTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;

        selectableHasChangedOperator = this.model.defaultTriggerOperators.find(operator => operator.id === TriggerOperator.HasChanged);
        selectableHasChangedOperator.isEnabled = this.model.shouldAllowHasChangedTriggerOperator;
    }

    private setIsEnabledActionTypes(): void {
        let actionType = this.model.actionTypes.find(actionType => actionType.id === ActionType.StyleFieldColor);
        actionType.isEnabled = this.model.shouldAllowStyleActionTypes;

        actionType = this.model.actionTypes.find(actionType => actionType.id === ActionType.SetMapPinColor);
        actionType.isEnabled = this.model.shouldAllowStyleActionTypes && !this.model.isMapPinUsed;

        actionType = this.model.actionTypes.find(actionType => actionType.id === ActionType.SetField);
        actionType.isEnabled = this.model.shouldAllowSetActionTypes;
    }

    private recursivelyFindIfTriggersWhereVisitedFromTriggerContainer(triggerContainer: UntypedFormGroup): boolean {
        const triggerArray = triggerContainer.get('trigger') as UntypedFormArray;
        if (!triggerArray || triggerArray.length === 0) {
            return false;
        }

        if (triggerArray.length === 1) {
            const nestedTrigger = triggerArray.at(0) as UntypedFormGroup;
            const operator = nestedTrigger.get('operator').value;
            if (operator) {
                return true;
            }
        }

        for (let i = 0; i < triggerArray.length; i++) {
            const nestedTriggerContainer = triggerArray.at(i) as UntypedFormGroup;
            const nestResult = this.recursivelyFindIfTriggersWhereVisitedFromTriggerContainer(nestedTriggerContainer);
            if (nestResult) {
                return nestResult;
            }
        }

        return false;
    }

    private recursivelyFindGridIdFromTriggerContainer(triggerContainer: UntypedFormGroup): string {
        const triggerArray = triggerContainer.get('trigger') as UntypedFormArray;
        if (!triggerArray || triggerArray.length === 0) {
            return '';
        }

        if (triggerArray.length === 1) {
            const nestedTrigger = triggerArray.at(0) as UntypedFormGroup;
            const nestedSelectedField = nestedTrigger.get('selectedField').value;
            if (nestedSelectedField?.gridColumnId) {
                return nestedSelectedField.fieldId;
            }
        }

        for (let i = 0; i < triggerArray.length; i++) {
            const nestedTriggerContainer = triggerArray.at(i) as UntypedFormGroup;
            const nestResult = this.recursivelyFindGridIdFromTriggerContainer(nestedTriggerContainer);
            if (nestResult) {
                return nestResult;
            }
        }

        return '';
    }

    private doesTriggersContainerHaveEverySelectedFieldAndRequiredRelationshipsPopulated(triggerProperty: UntypedFormArray): boolean {
        if (!triggerProperty?.length) {
            return;
        }

        return triggerProperty.controls.every((triggerContainer: UntypedFormGroup) => {
            const triggerArray = triggerContainer.get('trigger') as UntypedFormArray;
            if (!triggerArray?.length) {
                return;
            }

            if (triggerContainer.get('relationshipToPreceding').value === null &&
                triggerContainer.get('relationshipToPreceding').hasValidator(Validators.required)
            ) {
                return false;
            }

            if ((triggerArray.at(0) as UntypedFormGroup).get('trigger')) {
                return this.doesTriggersContainerHaveEverySelectedFieldAndRequiredRelationshipsPopulated(triggerArray);
            }

            return triggerArray.at(0).get('selectedField').value?.fieldId !== null;
        });
    }

    private doesTriggersContainerHaveHasChangedOperator(triggerProperty: UntypedFormArray): boolean {
        if (!triggerProperty?.length) {
            return;
        }

        const foundHasChangedSelector = triggerProperty.controls.find((triggerContainer: UntypedFormGroup) => {
            const triggerArray = triggerContainer.get('trigger') as UntypedFormArray;
            if (!triggerArray?.length) {
                return;
            }

            if ((triggerArray.at(0) as UntypedFormGroup).get('trigger')) {
                return this.doesTriggersContainerHaveHasChangedOperator(triggerArray);
            }

            return triggerArray.at(0).get('operator').value === TriggerOperator.HasChanged;
        });

        return foundHasChangedSelector !== undefined;
    }

    private resetDependentActionPropertiesAndValidity(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionProperties(logicSetIndex, actionIndex);
        this.resetDependentActionValidity(logicSetIndex, actionIndex);
        this._updateActionModeledField.next(true);
    }

    private resetDependentActionFieldPropertiesAndValidity(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionFieldProperties(logicSetIndex, actionIndex);
        this.resetDependentActionValidity(logicSetIndex, actionIndex);
    }

    private resetDependentActionSetDateTypePropertiesAndValidity(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionSetDateTypeProperties(logicSetIndex, actionIndex);
        this.resetDependentActionValidity(logicSetIndex, actionIndex);
    }

    private resetDependentActionEmailRecieptientTypePropertiesAndValidity(logicSetIndex: number, actionIndex: number): void {
        this.resetDependentActionEmailRecieptientTypeProperties(logicSetIndex, actionIndex);
        this.resetDependentActionValidity(logicSetIndex, actionIndex);
    }

    private resetDependentActionProperties(logicSetIndex: number, actionIndex: number): void {
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm) {
            return;
        }

        const action = actionsForm.at(actionIndex) as UntypedFormGroup;

        action.get('selectedField').setValue(null);
        action.get('setDateToToday').setValue(true);
        action.get('valueToSet').setValue(null);

        action.get('selectedField').markAsUntouched();
        action.get('setDateToToday').markAsUntouched();
        action.get('valueToSet').markAsUntouched();

        this.resetEmailProperties(action);
    }

    private resetDependentActionFieldProperties(logicSetIndex: number, actionIndex: number): void {
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm) {
            return;
        }

        const action = actionsForm.at(actionIndex) as UntypedFormGroup;

        action.get('setDateToToday').setValue(true);
        action.get('valueToSet').setValue(null);
    }

    private resetDependentActionSetDateTypeProperties(logicSetIndex: number, actionIndex: number): void {
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm) {
            return;
        }

        const action = actionsForm.at(actionIndex) as UntypedFormGroup;

        action.get('valueToSet').setValue(null);
    }

    private resetDependentActionEmailRecieptientTypeProperties(logicSetIndex: number, actionIndex: number): void {
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm) {
            return;
        }

        const action = actionsForm.at(actionIndex) as UntypedFormGroup;

        action.get('emailProperties').get('recipientField').setValue(null);
        action.get('emailProperties').get('recipientList').setValue(null);
    }

    private resetDependentActionValidity(logicSetIndex: number, actionIndex: number): void {
        const actionsForm = this.getActionControls(logicSetIndex);
        if (!actionsForm) {
            return;
        }

        const action = actionsForm.at(actionIndex) as UntypedFormGroup;
        if (!action) {
            return;
        }

        const actionType = action.get('actionType').value;

        switch (actionType) {
            case ActionType.SetField:
                this.resetEmailProperties(action);

                action.get('setDateToToday').setValidators([Validators.required]);
                action.get('selectedField').setValidators([Validators.required]);

                if (!action.get('selectedField').value?.fieldId) {
                    action.get('valueToSet').setValidators([Validators.required]);
                    break;
                }

                const selectedFieldTypeId = this.model.availableSetFields.find((field: SelectableField) => {
                    return field.identifier.fieldId === action.get('selectedField').value?.fieldId && field.identifier.gridColumnId === action.get('selectedField').value?.gridColumnId;
                })?.fieldTypeId;

                if (selectedFieldTypeId === FieldType.DateSelect && action.get('setDateToToday').value) {
                    action.get('valueToSet').clearValidators();
                    break;
                }

                action.get('valueToSet').setValidators([Validators.required]);
                break;
            case ActionType.StyleFieldColor:
                this.resetEmailProperties(action);

                action.get('valueToSet').setValidators([Validators.required]);
                action.get('selectedField').setValidators([Validators.required]);
                action.get('setDateToToday').clearValidators();
                break;
            case ActionType.SetMapPinColor:
                this.resetEmailProperties(action);

                action.get('valueToSet').setValidators([Validators.required]);
                action.get('selectedField').clearValidators();
                action.get('setDateToToday').clearValidators();
                break;
            case ActionType.SendEmail:
                this.setUpEmailPropertiesValidity(action);

                action.get('selectedField').clearValidators();
                action.get('valueToSet').clearValidators();
                action.get('setDateToToday').clearValidators();
                break;
        }

        action.get('selectedField').updateValueAndValidity();
        action.get('valueToSet').updateValueAndValidity();
        action.get('setDateToToday').updateValueAndValidity();
    }

    private resetEmailProperties(action: UntypedFormGroup): void {
        if (!action.get('emailProperties')) {
            return;
        }

        action.get('emailProperties').get('recipientType').setValue(RecipientType.List);
        action.get('emailProperties').get('recipientField').setValue(null);
        action.get('emailProperties').get('recipientList').setValue(null);
        action.get('emailProperties').get('sendWithBcc').setValue(false);
        action.get('emailProperties').get('richTextSubjectTemplate').setValue(null);
        action.get('emailProperties').get('richTextBodyTemplate').setValue(null);

        action.get('emailProperties').get('recipientType').clearValidators();
        action.get('emailProperties').get('recipientField').clearValidators();
        action.get('emailProperties').get('recipientList').clearValidators();
        action.get('emailProperties').get('sendWithBcc').clearValidators();
        action.get('emailProperties').get('richTextSubjectTemplate').clearValidators();

        this.updateValueAndValidityForEmailProperties(action);
    }

    private setUpEmailPropertiesValidity(action: UntypedFormGroup): void {
        if (!action.get('emailProperties')) {
            return;
        }

        action.get('emailProperties').get('recipientType').setValidators([Validators.required]);
        action.get('emailProperties').get('richTextSubjectTemplate').setValidators([Validators.required]);

        if (action.get('emailProperties').get('recipientType').value === RecipientType.List) {
            action.get('emailProperties').get('recipientField').clearValidators();
            action.get('emailProperties').get('recipientList').setValidators([Validators.required]);
        }
        else {
            action.get('emailProperties').get('recipientField').setValidators([Validators.required]);
            action.get('emailProperties').get('recipientList').clearValidators();
        }

        this.updateValueAndValidityForEmailProperties(action);
    }

    private updateValueAndValidityForEmailProperties(action: UntypedFormGroup): void {
        action.get('emailProperties').get('recipientType').updateValueAndValidity();
        action.get('emailProperties').get('recipientField').updateValueAndValidity();
        action.get('emailProperties').get('recipientList').updateValueAndValidity();
        action.get('emailProperties').get('sendWithBcc').updateValueAndValidity();
        action.get('emailProperties').get('richTextSubjectTemplate').updateValueAndValidity();
        action.get('emailProperties').get('richTextBodyTemplate').updateValueAndValidity();
    }

    private resetFieldDependentTriggerProperties(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        const triggersForm = this.getTriggerControls(logicSetIndex);
        if (!triggersForm) {
            return;
        }

        const triggerContainer = triggersForm.at(containerIndex) as UntypedFormGroup;
        let trigger = (triggerContainer.get('trigger') as UntypedFormArray).at(triggerIndex) as UntypedFormGroup;

        if (trigger?.get('trigger')) {
            trigger = (trigger.get('trigger') as UntypedFormArray).at(0) as UntypedFormGroup;
        }

        trigger.get('comparisonValue')?.setValue(null);
        trigger.get('comparisonValue')?.clearValidators();
        trigger.get('comparisonValue')?.updateValueAndValidity();

        trigger.get('operator')?.setValue(null);
    }

    private resetOperatorDependentTriggerPropertiesAndValidity(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        this.resetOperatorDependentTriggerProperties(logicSetIndex, containerIndex, triggerIndex);
        this.resetOperatorDependentTriggerValidity(logicSetIndex, containerIndex, triggerIndex);
    }

    private resetOperatorDependentTriggerProperties(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        const triggersForm = this.getTriggerControls(logicSetIndex);
        if (!triggersForm) {
            return;
        }

        const triggerContainer = triggersForm.at(containerIndex) as UntypedFormGroup;
        let trigger = (triggerContainer.get('trigger') as UntypedFormArray).at(triggerIndex) as UntypedFormGroup;
        if (trigger?.get('trigger')) {
            trigger = (trigger.get('trigger') as UntypedFormArray).at(0) as UntypedFormGroup;
        }

        trigger.get('comparisonValue')?.setValue(null);
    }

    private resetOperatorDependentTriggerValidity(logicSetIndex: number, containerIndex: number, triggerIndex: number): void {
        const triggersForm = this.getTriggerControls(logicSetIndex);
        if (!triggersForm) {
            return;
        }

        const triggerContainer = triggersForm.at(containerIndex) as UntypedFormGroup;
        if (!triggerContainer) {
            return;
        }

        let trigger = (triggerContainer.get('trigger') as UntypedFormArray).at(triggerIndex) as UntypedFormGroup;

        if (trigger?.get('trigger')) {
            trigger = (trigger.get('trigger') as UntypedFormArray).at(0) as UntypedFormGroup;
        }

        const selectedField = trigger.get('selectedField').value;
        const selectedFieldTypeId = this.model.availableTriggerFields.find((field: SelectableField) => {
            return field.identifier.fieldId === selectedField?.fieldId && field.identifier.gridColumnId === selectedField?.gridColumnId;
        })?.fieldTypeId;
        const operator = trigger.get('operator').value;
        const fieldTypeForComparisonValue = this.model.comparisonInputValueFieldTypes.get(selectedFieldTypeId)?.get(operator);

        if (!fieldTypeForComparisonValue) {
            trigger.get('comparisonValue').clearValidators();
        }
        else {
            trigger.get('comparisonValue').setValidators([Validators.required]);
        }

        trigger.get('comparisonValue').updateValueAndValidity();
    }

    private setAvailableSites(app: App, currentProject: Project, sites: Site[]): void {
        this.initializeSelectAllSite(app);

        this.model.siteDropdownInfo.availableSites = [this.model.siteDropdownInfo.selectAllSite];

        let sitesAssociatedWithProject = sites.filter((site: Site) => {
            return site.projects?.includes(currentProject.id);
        });

        sitesAssociatedWithProject = sitesAssociatedWithProject.map((site: Site) => {
            site.dropdownName = `${site.siteNo} - ${site.siteName}`;
            return site;
        });

        this.model.siteDropdownInfo.availableSites = this.model.siteDropdownInfo.availableSites.concat(sitesAssociatedWithProject);
        this.model.siteDropdownInfo.filteredSites = [...this.model.siteDropdownInfo.availableSites];
    }

    private initializeSelectAllSite(app: App): void {
        this.model.siteDropdownInfo.selectAllSite.siteNo = this.model.siteDropdownInfo.selectAllSiteNo;
        this.model.siteDropdownInfo.selectAllSite.id = this.model.siteDropdownInfo.selectAllSiteNo;
        this.model.siteDropdownInfo.selectAllSite.siteType = SiteTypes.Control;
        this.model.siteDropdownInfo.selectAllSite.dropdownName = this.model.siteDropdownInfo.selectAllMessage + app.aliases.site.plural.toLowerCase();
    }

    private setModeledSitesForNewLogicBuilder(): void {
        this.model.siteDropdownInfo.modeledSites = this.model.siteDropdownInfo.availableSites.map((site: Site) => {
            return site.id;
        });
        this.model.siteDropdownInfo.selectedSites = [...this.model.siteDropdownInfo.modeledSites];
        this.model.siteDropdownInfo.numberOfSelectedSites = this.model.siteDropdownInfo.selectedSites.length - 1;
    }

    private setModeledSitesForExistingLogicBuilder(logicBuilder: LogicBuilder): void {
        if (!logicBuilder.siteIds?.length) {
            this.setModeledSitesForNewLogicBuilder();
            return;
        }

        const filteredSites = this.model.siteDropdownInfo.availableSites.filter((site: Site) => {
            return logicBuilder.siteIds.includes(site.id);
        });

        //If all the selected site have been deleted
        if (filteredSites.length === 0) {
            this.model.siteDropdownInfo.modeledSites = [];
            this.model.siteDropdownInfo.selectedSites = [];
            this.model.siteDropdownInfo.numberOfSelectedSites = 0;
            return;
        }

        //If all the the non selected sites have been deleted
        if (filteredSites.length === this.model.siteDropdownInfo.availableSites.length - 1) {
            this.setModeledSitesForNewLogicBuilder();
            return;
        }

        this.model.siteDropdownInfo.modeledSites = filteredSites.map((site: Site) => {
            return site.id;
        });

        this.model.siteDropdownInfo.selectedSites = [...this.model.siteDropdownInfo.modeledSites];
        this.model.siteDropdownInfo.numberOfSelectedSites = this.model.siteDropdownInfo.selectedSites.length;
    }

    private associateSiteWithLogicBuilder(siteId: string): void {
        this.model.siteDropdownInfo.selectedSites = this.model.siteDropdownInfo.selectedSites.filter((siteId: string) => {
            return siteId !== this.model.siteDropdownInfo.selectAllSite.id;
        });

        this.model.siteDropdownInfo.selectedSites.push(siteId);
        this.model.siteDropdownInfo.modeledSites = [...this.model.siteDropdownInfo.selectedSites];
        this.model.siteDropdownInfo.numberOfSelectedSites++;
        this.associateTheSelectAllSiteIfAllSitesHaveBeenAssociated();
    }

    private associateTheSelectAllSiteIfAllSitesHaveBeenAssociated(): void {
        if (this.model.siteDropdownInfo.selectedSites.length !== this.model.siteDropdownInfo.availableSites.length - 1) {
            return;
        }

        this.associateAllSitesWithLogicBuilder();
    }

    private associateAllSitesWithLogicBuilder(): void {
        this.model.siteDropdownInfo.modeledSites = this.model.siteDropdownInfo.availableSites.map((site: Site) => {
            return site.id;
        });

        this.model.siteDropdownInfo.selectedSites = [...this.model.siteDropdownInfo.modeledSites];
        this.model.siteDropdownInfo.numberOfSelectedSites = this.model.siteDropdownInfo.selectedSites.length - 1;
    }

    private disassociateAllSitesFromLogicBuilder(): void {
        this.model.siteDropdownInfo.selectedSites = [];
        this.model.siteDropdownInfo.modeledSites = [];
        this.model.siteDropdownInfo.numberOfSelectedSites = 0;
    }

    private getLogicBuilderFromLogicBuilderFormModel(logicBuilderFormModel: LogicBuilderFormModel): LogicBuilder {
        const logicBuilder = new LogicBuilder();
        logicBuilder.id = logicBuilderFormModel.id;
        logicBuilder.name = logicBuilderFormModel.name;
        logicBuilder.description = logicBuilderFormModel.description;
        logicBuilder.siteIds = logicBuilderFormModel.siteIds;
        logicBuilder.createdById = logicBuilderFormModel.createdById;
        logicBuilder.createdByName = logicBuilderFormModel.createdByName;
        logicBuilder.projectId = logicBuilderFormModel.projectId;
        logicBuilder.active = logicBuilderFormModel.active;
        logicBuilder.logicBuilderSets = this.getLogicBuilderSetsFromLogicBuilderSetsFormModel(logicBuilderFormModel.logicBuilderSets);
        logicBuilder.siteEmailNotificationsTriggered = logicBuilderFormModel.siteEmailNotificationsTriggered;

        return logicBuilder;
    }

    private getLogicBuilderSetsFromLogicBuilderSetsFormModel(logicBuilderSetsFormModel: LogicBuilderSetFormModel[]): LogicBuilderSet[] {
        const logicBuilderSets: LogicBuilderSet[] = [];

        logicBuilderSetsFormModel.forEach((logicBuilderSet: LogicBuilderSetFormModel) => {
            const logicBuilderSetConverted = this.convertLogicBuilderSetFormModelToLogicBuilderSet(logicBuilderSet);
            logicBuilderSets.push(logicBuilderSetConverted);
        });

        return logicBuilderSets;
    }

    private convertLogicBuilderSetFormModelToLogicBuilderSet(logicBuilderSetFormModel: LogicBuilderSetFormModel): LogicBuilderSet {
        const logicBuilderSet: LogicBuilderSet = new LogicBuilderSet();
        logicBuilderSet.actions = this.convertActionsFromFormModelToActions(logicBuilderSetFormModel.actions);

        logicBuilderSet.triggers = this.convertTriggerFormModelToTriggers(logicBuilderSetFormModel.triggers);

        return logicBuilderSet;
    }

    convertActionsFromFormModelToActions(actionsFormModel: Action[]): Action[] {
        return actionsFormModel.map((actionFormModel: Action) => {
            const action: Action = new Action();
            action.actionType = actionFormModel.actionType;
            action.selectedField = actionFormModel.selectedField;
            action.setDateToToday = actionFormModel.setDateToToday;
            action.valueToSet = actionFormModel.valueToSet;
            if (action.actionType === ActionType.SendEmail) {
                action.emailProperties = actionFormModel.emailProperties;
            }

            return action;
        });
    }

    private convertTriggerFormModelToTriggers(triggersContainersForm: RecursiveTriggerContainerFormModel[]): RecursiveTriggerContainer[] {
        return triggersContainersForm.map((triggerContainerForm: RecursiveTriggerContainerFormModel) => {
            const triggerFormArray = triggerContainerForm.trigger;

            if (!Array.isArray(triggerFormArray) || triggerFormArray.length === 0) {
                throw new Error('Trigger array is empty or invalid');
            }

            const isGroup = 'trigger' in triggerFormArray[0];
            if (isGroup) {
                const nested = this.convertTriggerFormModelToTriggers(triggerFormArray as RecursiveTriggerContainerFormModel[]);

                const triggerContainer: RecursiveTriggerContainer = {
                    nestedTriggerContainers: nested,
                    relationshipToPreceding: triggerContainerForm.relationshipToPreceding
                };

                return triggerContainer;

            }

            const triggerContainer: RecursiveTriggerContainer = {
                relationshipToPreceding: triggerContainerForm.relationshipToPreceding,
                trigger: triggerFormArray[0] as Trigger
            };

            return triggerContainer;

        });
    }

    private setIsDynamicLogic(): void {
        if (this.logicBuilderSets?.length === 1) {
            return;
        }

        this.model.doesLogicBuilderContainDynamicLogic = true;

        const logicBuilderSet = this.logicBuilderSets.at(0) as UntypedFormGroup;
        const actions = logicBuilderSet?.get('actions') as UntypedFormArray;
        const actionType = actions?.at(0)?.get('actionType')?.value;
        if (actionType === ActionType.StyleFieldColor) {
            this.model.doesLogicBuilderContainDynamicStyleFieldLogic = true;
            return;
        }

        this.model.doesLogicBuilderContainDynamicMapPinStyling = true;
    }

    private formTriedSaving(): void {
        this.form.markAllAsTouched();
        this._formTriedSaving.next(true);
    }

    private couldEmailNotificationsBeReset(logicBuilder: LogicBuilder): boolean {
        if (logicBuilder.id && logicBuilder.logicBuilderSets[0].actions.some((action: Action) => action.actionType === ActionType.SendEmail) && logicBuilder.logicBuilderSets[0].triggers[0].trigger.selectedField.gridColumnId) {
            return true;
        }

        return false;
    }

    private resetEmailNotificationSentList(logicBuilder: LogicBuilder): void {
        if (logicBuilder.logicBuilderSets[0].actions.some((action: Action) => action.actionType === ActionType.SendEmail)) {
            logicBuilder.siteEmailNotificationsTriggered = [];
        }
    }

    private doesTriggersContainerHaveFormulaField(triggerProperty: UntypedFormArray): boolean {
        if (!triggerProperty?.length) {
            return false;
        }

        const isFormulaFieldInTriggers = triggerProperty.controls.find((triggerContainer: UntypedFormGroup) => {
            const triggerArray = triggerContainer.get('trigger') as UntypedFormArray;
            if (!triggerArray?.length) {
                return false;
            }

            if ((triggerArray.at(0) as UntypedFormGroup).get('trigger')) {
                return this.doesTriggersContainerHaveFormulaField(triggerArray);
            }

            const selectedField = triggerArray.at(0).get('selectedField').value;
            if (!selectedField?.fieldId) {
                return false;
            }

            const field = this.model.availableTriggerFields.find((field: SelectableField) => {
                return field.identifier.fieldId === selectedField.fieldId &&
                    field.identifier.gridColumnId === selectedField.gridColumnId;
            });

            return field?.fieldTypeId === FieldType.Formula;
        });

        return isFormulaFieldInTriggers !== undefined;
    }
}
